import { type ComponentProps, Fragment, type ReactNode } from 'react'
import { useChangeBackgroundColor } from '#app/hooks/use-change-background-color.ts'
import { useIsMounted } from '#app/hooks/use-is-mounted.ts'
import { useMainStore } from '#app/stores/main'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { useMediaQueryUnitMap } from '#app/utils/media-query.ts'
import { cn } from '#app/utils/misc.tsx'

interface LandingSectionProps {
	title?: string[]
	borderToBorderOnX?: boolean
	children: ReactNode
	containerClassName?: string
	backgroundColor?: string
}

export function LandingSection({
	title,
	borderToBorderOnX = false,
	containerClassName,
	backgroundColor = extendedTheme.colors.background,
	...props
}: Omit<ComponentProps<'section'>, 'title'> & LandingSectionProps) {
	const mounted = useIsMounted()
	const spacing = useMediaQueryUnitMap(
		extendedTheme.spacing.xl,
		extendedTheme.spacing.lg,
		extendedTheme.spacing.xxl,
		extendedTheme.spacing.xl,
		extendedTheme.spacing.xxxl,
		'8vh',
		'16vh',
	)

	const sectionRef = useChangeBackgroundColor(backgroundColor)
	const currentBackgroundColor = useMainStore.use.backgroundColor()

	// Use default spacing during SSR/hydration, then switch to responsive spacing
	const effectiveSpacing = mounted ? spacing : extendedTheme.spacing.xl

	return (
		<section
			{...props}
			ref={sectionRef}
			style={{
				gridTemplateRows: `${effectiveSpacing} 1fr ${effectiveSpacing}`,
				...props.style,
			}}
			className={cn(
				'min-h-[90vh]',
				`grid grid-cols-12`,
				currentBackgroundColor !== backgroundColor &&
					'mix-blend-multiply transition-colors duration-300 ease-in-out',
				props.className,
			)}
		>
			<div
				style={{
					rowGap: effectiveSpacing,
				}}
				className={cn(
					'col-start-1 -col-end-1 row-start-2 -row-end-2 lg:col-start-2 lg:-col-end-2',
					'flex flex-col',
					borderToBorderOnX ? 'lg:col-start-1 lg:-col-end-1' : 'container',
					containerClassName,
				)}
			>
				{title && (
					<h2
						className={
							'text-body-2sm space-x-2 font-light lowercase xl:text-body-sm'
						}
					>
						{title.map((item, index, array) => (
							<Fragment key={item}>
								<span>/</span>{' '}
								<span
									className={cn(
										index === array.length - 1 && 'font-medium',
										'tracking-[0.2em]',
									)}
								>
									{item}
								</span>
							</Fragment>
						))}{' '}
						{title.length === 1 && <span>/</span>}
					</h2>
				)}

				<div className={'flex h-full flex-col justify-center'}>
					{props.children}
				</div>
			</div>
		</section>
	)
}
